/**
 * Design tokens — the single source of truth for the proposal visual system.
 *
 * These feed three consumers:
 *   1. the MUI theme (src/theme/index.js) for the web proposal
 *   2. CSS custom properties (emitted by cssVariables() below) for plain CSS
 *      and, critically, for the print route the PDF renderer loads
 *   3. any component that needs a raw value
 *
 * The palette is derived from the brand purple #784593 that already carried the
 * proposals; the ramps around it are new, so surfaces, borders and text finally
 * have defined steps instead of 171 ad-hoc hex values.
 */

// ---------------------------------------------------------------------------
// Colour
// ---------------------------------------------------------------------------

const brand = {
  50: '#f6f1fa',
  100: '#ebdff3',
  200: '#d8c1e8',
  300: '#bd98d7',
  400: '#9d6bc1',
  500: '#8853a6',
  600: '#784593', // the established LingoYou purple
  700: '#63377a',
  800: '#4e2c60',
  900: '#3a2148',
  950: '#241430'
};

// Deep violet used for emphasis moments (cover, selected package, CTAs).
const violet = {
  400: '#a158d6',
  500: '#8a2fc9',
  600: '#6400a9',
  700: '#520089'
};

const accent = {
  50: '#eef6fd',
  100: '#d6eafa',
  200: '#aed5f4',
  300: '#7bbaeb',
  400: '#499ad4', // established secondary blue — decorative use only
  500: '#2da0f0',
  // White text on 400 is 3.06:1, below WCAG AA. Interactive fills therefore
  // use 600 (4.73:1); 400 stays available for rules, icons and accents.
  600: '#2277bd',
  700: '#1c5c92'
};

const success = {
  50: '#e9f6ee',
  100: '#c9e9d5',
  300: '#6bbd8a',
  500: '#139448',
  600: '#008938', // established green, nudged to clear 4.5:1 against white
  700: '#006f31'
};

const warning = {
  50: '#fdf4e7',
  100: '#f9e3bd',
  500: '#d98c1f',
  600: '#b06f13',
  700: '#8a560c'
};

const danger = {
  50: '#fdeded',
  100: '#f9d2d2',
  500: '#d24444', // clears 4.5:1 against white text
  600: '#b93030',
  700: '#912323'
};

// Warm-grey neutrals; tuned to sit calmly beside the purple rather than the
// pure-grey #626168 / #414042 / #3c3c3b trio that was in use.
const neutral = {
  0: '#ffffff',
  25: '#fcfbfd',
  50: '#f7f6f9',
  100: '#efedf2',
  200: '#e2dfe8',
  300: '#cbc7d3',
  400: '#a5a0b0',
  // Tertiary text sits on surfaces up to #f7f6f9; this clears 4.5:1 on all of
  // them (the previous #7d7889 measured 3.96:1 on the sunken surface).
  500: '#736e7e',
  600: '#5d5868',
  700: '#454150',
  800: '#302d39',
  900: '#1f1d26',
  950: '#131118'
};

export const palette = {
  brand,
  violet,
  accent,
  success,
  warning,
  danger,
  neutral,

  // Semantic aliases — components should reach for these, not the ramps.
  text: {
    primary: neutral[900],
    secondary: neutral[600],
    tertiary: neutral[500],
    inverse: neutral[0],
    brand: brand[600],
    onBrand: neutral[0]
  },
  surface: {
    page: neutral[25],
    raised: neutral[0],
    sunken: neutral[50],
    subtle: neutral[100],
    brand: brand[600],
    brandSubtle: brand[50],
    inverse: neutral[900]
  },
  border: {
    // Decorative separators. WCAG 1.4.11 does not apply to these, so they can
    // stay light enough not to make the layout feel heavy.
    subtle: neutral[200],
    default: neutral[300],
    strong: neutral[400],
    brand: brand[300],
    // Where a border is the only thing identifying a control (inputs, outlined
    // buttons, checkboxes), 1.4.11 requires 3:1 against the adjacent surface.
    // Clears it on all three: white 3.26, page 3.16, sunken 3.03.
    interactive: '#908d96',
    focus: brand[500]
  }
};

// ---------------------------------------------------------------------------
// Typography
// ---------------------------------------------------------------------------

export const fonts = {
  // Legian is the brand display face; Montserrat carries body copy.
  display: "'Legian', 'Montserrat', 'Segoe UI', sans-serif",
  body: "'Montserrat', 'Segoe UI', system-ui, -apple-system, sans-serif",
  // Retained for the signature/handwritten accent on the acceptance block.
  script: "'Allura', cursive",
  mono: "'SFMono-Regular', 'Consolas', 'Liberation Mono', monospace"
};

/**
 * Fluid type scale. Each step interpolates between a phone size and a desktop
 * size, which is what lets the phone layout use real, readable type instead of
 * the old approach of scaling a 1500px canvas down to 27%.
 */
export const fontSize = {
  '2xs': 'clamp(0.625rem, 0.60rem + 0.12vw, 0.6875rem)', // 10 → 11
  xs: 'clamp(0.6875rem, 0.66rem + 0.14vw, 0.75rem)', //     11 → 12
  sm: 'clamp(0.8125rem, 0.78rem + 0.16vw, 0.875rem)', //    13 → 14
  base: 'clamp(0.9375rem, 0.90rem + 0.19vw, 1rem)', //      15 → 16
  lg: 'clamp(1.0625rem, 1.01rem + 0.28vw, 1.1875rem)', //   17 → 19
  xl: 'clamp(1.1875rem, 1.10rem + 0.44vw, 1.375rem)', //    19 → 22
  '2xl': 'clamp(1.375rem, 1.24rem + 0.68vw, 1.75rem)', //   22 → 28
  '3xl': 'clamp(1.625rem, 1.40rem + 1.14vw, 2.25rem)', //   26 → 36
  '4xl': 'clamp(1.875rem, 1.49rem + 1.93vw, 2.875rem)', //  30 → 46
  '5xl': 'clamp(2.25rem, 1.66rem + 2.95vw, 3.75rem)', //    36 → 60
  '6xl': 'clamp(2.75rem, 1.93rem + 4.09vw, 4.75rem)' //     44 → 76
};

export const fontWeight = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800
};

export const lineHeight = {
  none: 1,
  tight: 1.15,
  snug: 1.3,
  normal: 1.5,
  relaxed: 1.65,
  loose: 1.8
};

export const letterSpacing = {
  tighter: '-0.03em',
  tight: '-0.015em',
  normal: '0',
  wide: '0.02em',
  wider: '0.06em',
  widest: '0.14em' // for overlines / eyebrow labels
};

// ---------------------------------------------------------------------------
// Space, radius, elevation, motion
// ---------------------------------------------------------------------------

/** 4px base step. space[4] === '1rem'. */
export const space = {
  0: '0',
  px: '1px',
  0.5: '0.125rem',
  1: '0.25rem',
  2: '0.5rem',
  3: '0.75rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  8: '2rem',
  10: '2.5rem',
  12: '3rem',
  16: '4rem',
  20: '5rem',
  24: '6rem',
  32: '8rem',
  40: '10rem'
};

export const radius = {
  none: '0',
  sm: '0.25rem',
  md: '0.5rem',
  lg: '0.75rem',
  xl: '1rem',
  '2xl': '1.5rem',
  '3xl': '2rem',
  full: '9999px'
};

/**
 * Real elevation. The previous PDF engine could not draw a shadow at all, which
 * is the main reason the documents read as flat; rendering through Chromium
 * means these now appear in the PDF exactly as they do on screen.
 */
export const shadow = {
  none: 'none',
  xs: '0 1px 2px rgba(31, 29, 38, 0.06)',
  sm: '0 1px 3px rgba(31, 29, 38, 0.08), 0 1px 2px rgba(31, 29, 38, 0.04)',
  md: '0 4px 12px rgba(31, 29, 38, 0.08), 0 2px 4px rgba(31, 29, 38, 0.04)',
  lg: '0 12px 28px rgba(31, 29, 38, 0.10), 0 4px 10px rgba(31, 29, 38, 0.05)',
  xl: '0 24px 48px rgba(31, 29, 38, 0.12), 0 8px 16px rgba(31, 29, 38, 0.06)',
  brand: '0 10px 30px rgba(120, 69, 147, 0.28)',
  brandLg: '0 20px 48px rgba(120, 69, 147, 0.34)',
  focus: `0 0 0 3px ${brand[200]}`
};

export const gradient = {
  brand: `linear-gradient(135deg, ${brand[600]} 0%, ${violet[600]} 100%)`,
  brandSoft: `linear-gradient(135deg, ${brand[50]} 0%, ${accent[50]} 100%)`,
  accent: `linear-gradient(135deg, ${accent[400]} 0%, ${brand[500]} 100%)`,
  sheen: 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 100%)',
  // Used behind cover art so light text stays legible over photography.
  scrim: 'linear-gradient(180deg, rgba(19,17,24,0) 0%, rgba(19,17,24,0.72) 100%)'
};

export const motion = {
  duration: { instant: '80ms', fast: '150ms', normal: '240ms', slow: '400ms' },
  easing: {
    standard: 'cubic-bezier(0.2, 0, 0, 1)',
    decelerate: 'cubic-bezier(0, 0, 0, 1)',
    accelerate: 'cubic-bezier(0.3, 0, 1, 1)',
    spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)'
  }
};

/** Breakpoints in px. `md` is the web layout's phone/desktop switch. */
export const breakpoint = { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 };

/** Page geometry for the print route. A4 at 96dpi, with a real margin. */
export const page = {
  width: '210mm',
  height: '297mm',
  margin: '14mm',
  contentWidth: '182mm' // 210 − (2 × 14)
};

export const zIndex = {
  base: 0,
  raised: 10,
  sticky: 100,
  header: 200,
  drawer: 300,
  modal: 400,
  toast: 500
};

// ---------------------------------------------------------------------------
// CSS custom properties
// ---------------------------------------------------------------------------

const flatten = (obj, prefix, out = {}) => {
  for (const [key, value] of Object.entries(obj)) {
    const name = `${prefix}-${String(key).replace(/\./g, '_')}`;
    if (value && typeof value === 'object') flatten(value, name, out);
    else out[name] = value;
  }
  return out;
};

/** Every token as a flat `--ly-*` custom-property map. */
export const cssVariables = () => ({
  ...flatten(palette, '--ly-color'),
  ...flatten(fonts, '--ly-font'),
  ...flatten(fontSize, '--ly-text'),
  ...flatten(fontWeight, '--ly-weight'),
  ...flatten(lineHeight, '--ly-leading'),
  ...flatten(letterSpacing, '--ly-tracking'),
  ...flatten(space, '--ly-space'),
  ...flatten(radius, '--ly-radius'),
  ...flatten(shadow, '--ly-shadow'),
  ...flatten(gradient, '--ly-gradient'),
  ...flatten(motion, '--ly-motion'),
  ...flatten(page, '--ly-page'),
  ...flatten(zIndex, '--ly-z')
});

/** The same map serialised into a `:root { … }` block. */
export const cssVariablesText = () => {
  const vars = cssVariables();
  const body = Object.entries(vars)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join('\n');
  return `:root {\n${body}\n}`;
};

const tokens = {
  palette,
  fonts,
  fontSize,
  fontWeight,
  lineHeight,
  letterSpacing,
  space,
  radius,
  shadow,
  gradient,
  motion,
  breakpoint,
  page,
  zIndex
};

export default tokens;
